import { useEffect, useRef, useState } from 'react'
import type {
  BorderEdge,
  BorderLine,
  CenteringProjection,
  CenteringProjectionAxis,
  CenteringProjectionGeometry,
  CenteringProjectionSection,
  CenteringSide,
  DamageBox,
  DamageDetection,
  GradeSideAnalysis,
} from '../../lib/types'
import {
  buildCenteringBorderGeometry,
  isCenteringCurveVisibleInImage,
  resolveCenteringProjectionDisplaySide,
} from './advancedCenteringClipping'
import {
  buildCenteringCurvePath,
  getSparseCenteringCurveRanges,
  getCenteringCurveMidpoint,
  isValidCenteringCurve,
} from './advancedCenteringCurve'
import {
  getAppliedImageRectification,
  scaleImageRectificationSourceShape,
  transformCenteringProjectionForDisplay,
  transformImageBoxForRectifiedDisplay,
  transformSvgPathsForRectifiedDisplay,
} from './gradeImageRectification'
import styles from './slab.module.css'

type Size = { width: number; height: number }
type Fit = { scaleX: number; scaleY: number; offsetX: number; offsetY: number; width: number; height: number }
type Rect = { x: number; y: number; width: number; height: number }
type Segment = { x1: number; y1: number; x2: number; y2: number }
type SideSpan = { start: number; end: number }

const DAMAGE_COLOR = 'rgba(255, 213, 0, 0.92)'
const DAMAGE_FILL = 'rgba(255, 213, 0, 0.48)'
const CENTERING_COLOR = 'rgba(255, 140, 0, 1)'
const CENTERING_WARNING = 'rgba(243, 150, 0, 1)'
const CENTERING_FILL = 'rgba(255, 140, 0, 0.25)'
const CENTERING_LINE_WIDTH = 0.5
const MAX_LINE_LENGTH_RATIO = 0.25
const PROJECTION_SIDES = ['left', 'right', 'top', 'bottom'] as const
const SPARSE_BOUNDARY_LINE_WIDTH = 2.5
const SPARSE_CONNECTOR_LINE_WIDTH = 1
const SPARSE_LINE_DASH = [2, 2] as const
const LABEL_WIDTH = 60
const LABEL_HEIGHT = 19
const LABEL_GAP = 4

export function AnalysisOverlayCanvas({
  analysis,
  centering,
  imageUrl,
  rectified = false,
  className,
}: {
  analysis?: GradeSideAnalysis
  centering?: CenteringSide
  imageUrl: string
  rectified?: boolean
  className?: string
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [imageSize, setImageSize] = useState<Size | null>(null)

  useEffect(() => {
    if (!imageUrl) return
    let cancelled = false
    const img = new Image()
    img.onload = () => {
      if (!cancelled) setImageSize({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.src = imageUrl
    return () => {
      cancelled = true
    }
  }, [imageUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    const parent = canvas?.parentElement
    if (!canvas || !parent) return

    const render = () => drawOverlay(canvas, analysis, centering, imageSize, rectified)
    render()

    const observer = new ResizeObserver(render)
    observer.observe(parent)
    return () => observer.disconnect()
  }, [analysis, centering, imageSize, rectified])

  return <canvas ref={canvasRef} className={`${styles.analysisCanvas} ${className ?? ''}`} />
}

function drawOverlay(
  canvas: HTMLCanvasElement,
  analysis: GradeSideAnalysis | undefined,
  centering: CenteringSide | undefined,
  imageSize: Size | null,
  rectified: boolean,
) {
  const parent = canvas.parentElement
  const ctx = canvas.getContext('2d')
  if (!parent || !ctx) return

  const rect = parent.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  canvas.width = Math.max(1, Math.round(rect.width * dpr))
  canvas.height = Math.max(1, Math.round(rect.height * dpr))
  canvas.style.width = `${rect.width}px`
  canvas.style.height = `${rect.height}px`
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, rect.width, rect.height)

  if (!analysis) return
  const displayAnalysis = prepareAnalysisForDisplay(analysis, rectified)
  if (!displayAnalysis) return

  const sourceWidth = positive(displayAnalysis.sourceWidth) || imageSize?.width || rect.width
  const sourceHeight = positive(displayAnalysis.sourceHeight) || imageSize?.height || rect.height
  if (!sourceWidth || !sourceHeight) return

  const fit = calculateContainFit(sourceWidth, sourceHeight, rect.width, rect.height)
  drawDamage(ctx, displayAnalysis, fit, sourceWidth, sourceHeight)
  const projection = displayAnalysis.centeringProjection
  if (projection?.version === 2) {
    drawProjectedCentering(ctx, projection, displayAnalysis, rect.width, rect.height)
    return
  }
  if (projection && canRenderProjection(projection)) {
    drawProjectedCentering(ctx, projection, displayAnalysis, rect.width, rect.height)
    return
  }
  drawLegacyCentering(ctx, displayAnalysis, centering, fit)
}

function prepareAnalysisForDisplay(
  analysis: GradeSideAnalysis,
  rectified: boolean,
): GradeSideAnalysis | null {
  if (!rectified) return analysis

  const projection = analysis.centeringProjection
  if (!projection || !isProjectionStructureValid(projection)) return null
  const imageRectification = getAppliedImageRectification(projection)
  if (!imageRectification) return null

  const transformedCentering = transformCenteringProjectionForDisplay(
    projection,
    projectionToGeometry(projection),
    imageRectification,
  )
  if (!transformedCentering) return null

  const sourceWidth = positive(analysis.sourceWidth) ?? imageRectification.source_shape[1]
  const sourceHeight = positive(analysis.sourceHeight) ?? imageRectification.source_shape[0]
  const damageRectification = scaleImageRectificationSourceShape(imageRectification, [
    sourceHeight,
    sourceWidth,
  ])
  const damageMaskPaths = damageRectification
    ? transformSvgPathsForRectifiedDisplay(analysis.damageMaskPaths, damageRectification).paths
    : []

  return {
    ...analysis,
    sourceWidth: imageRectification.display_shape[1],
    sourceHeight: imageRectification.display_shape[0],
    damageMaskPaths,
    damageDetections:
      damageMaskPaths.length > 0
        ? []
        : transformDamageDetections(
            analysis.damageDetections,
            sourceWidth,
            sourceHeight,
            imageRectification,
            damageRectification,
          ),
    centeringProjection: transformedCentering.projection,
  }
}

function transformDamageDetections(
  detections: readonly DamageDetection[] | undefined,
  sourceWidth: number,
  sourceHeight: number,
  imageRectification: NonNullable<CenteringProjection['image_rectification']>,
  damageRectification: NonNullable<CenteringProjection['image_rectification']> | null,
): GradeSideAnalysis['damageDetections'] {
  return (detections ?? []).flatMap((detection) => {
    const maxX = Math.max(Math.abs(detection.box.x1), Math.abs(detection.box.x2))
    const maxY = Math.max(Math.abs(detection.box.y1), Math.abs(detection.box.y2))
    const usesDamageCoordinates =
      (maxX <= 1.5 && maxY <= 1.5) ||
      (maxX <= sourceWidth * 1.5 && maxY <= sourceHeight * 1.5)
    const rectification = usesDamageCoordinates ? damageRectification : imageRectification
    const width = usesDamageCoordinates ? sourceWidth : imageRectification.source_shape[1]
    const height = usesDamageCoordinates ? sourceHeight : imageRectification.source_shape[0]
    const rect = rectification ? normalizeBox(detection.box, width, height) : null
    if (!rectification || !rect) return []

    const transformed = transformImageBoxForRectifiedDisplay(
      {
        x1: rect.x,
        y1: rect.y,
        x2: rect.x + rect.width,
        y2: rect.y + rect.height,
      },
      rectification,
    )
    if (!transformed) return []
    return [
      {
        ...detection,
        box: transformed,
      },
    ]
  })
}

function drawDamage(
  ctx: CanvasRenderingContext2D,
  analysis: GradeSideAnalysis,
  fit: Fit,
  sourceWidth: number,
  sourceHeight: number,
) {
  const maskPaths = analysis.damageMaskPaths ?? []
  if (maskPaths.length > 0) {
    maskPaths.forEach((svgPath) => drawDamagePath(ctx, svgPath, fit, sourceWidth, sourceHeight))
    return
  }

  const detections = analysis.damageDetections ?? []
  detections.forEach((detection) => {
    const box = normalizeBox(detection.box, sourceWidth, sourceHeight)
    if (!box) return
    drawDamageRect(ctx, sourceRectToDisplayRect(box, fit))
  })
}

function drawDamagePath(
  ctx: CanvasRenderingContext2D,
  svgPath: string,
  fit: Fit,
  sourceWidth: number,
  sourceHeight: number,
) {
  const rect = parseRectangularPath(svgPath)
  if (rect) {
    drawDamageRect(ctx, sourceRectToDisplayRect(padDamageRect(rect, sourceWidth, sourceHeight), fit))
    return
  }

  try {
    const path = new Path2D(svgPath)
    ctx.save()
    ctx.translate(fit.offsetX, fit.offsetY)
    ctx.scale(fit.scaleX, fit.scaleY)
    ctx.fillStyle = DAMAGE_FILL
    ctx.fill(path)
    ctx.restore()
  } catch {
    // Invalid persisted SVG path; skip it.
  }
}

function drawDamageRect(ctx: CanvasRenderingContext2D, rect: Rect) {
  if (rect.width <= 0 || rect.height <= 0) return
  const shortSide = Math.min(rect.width, rect.height)
  const strokeWidth = clamp(shortSide * 0.085, 2, 4)
  const radius = clamp(shortSide * 0.22, 2, 18)
  ctx.save()
  ctx.strokeStyle = DAMAGE_COLOR
  ctx.lineWidth = strokeWidth
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  roundedRect(ctx, rect.x, rect.y, rect.width, rect.height, radius)
  ctx.stroke()
  ctx.restore()
}

function drawProjectedCentering(
  ctx: CanvasRenderingContext2D,
  projection: CenteringProjection,
  analysis: GradeSideAnalysis,
  containerWidth: number,
  containerHeight: number,
) {
  if (!isProjectionStructureValid(projection)) return

  const [sourceHeight, sourceWidth] = projection.image_shape
  const fit = calculateContainFit(sourceWidth, sourceHeight, containerWidth, containerHeight)
  const geometry = projectionToGeometry(projection)
  const candidate = geometry ? buildCenteringBorderGeometry(geometry.sides) : null
  const completeBoundaries = Boolean(
    candidate?.outerBoundary &&
      candidate.innerBoundary &&
      candidate.bands &&
      PROJECTION_SIDES.every(
        (side) =>
          isCenteringCurveVisibleInImage(candidate.outerBoundary?.sides[side], sourceWidth, sourceHeight) &&
          isCenteringCurveVisibleInImage(candidate.innerBoundary?.sides[side], sourceWidth, sourceHeight),
      ),
  )
  const outerBoundary = completeBoundaries ? candidate?.outerBoundary ?? null : null
  const innerBoundary = completeBoundaries ? candidate?.innerBoundary ?? null : null
  const displaySide = (side: BorderEdge) =>
    resolveCenteringProjectionDisplaySide(
      projection.sides[side],
      geometry?.sides[side],
      completeBoundaries,
      sourceWidth,
      sourceHeight,
    )

  ctx.save()
  applySourceFit(ctx, fit)
  clipProjectedOcclusions(ctx, analysis, sourceWidth, sourceHeight)
  if (outerBoundary) clipToBoundary(ctx, outerBoundary.sidePoints)
  PROJECTION_SIDES.forEach((side) => {
    const sideGeometry = displaySide(side)
    const line = innerBoundary?.sides[side] ?? sideGeometry?.inner_line
    if (line) drawSparseCurve(ctx, line, sourceWidth, fit.scaleX)
  })
  ctx.restore()

  ctx.save()
  applySourceFit(ctx, fit)
  clipProjectedOcclusions(ctx, analysis, sourceWidth, sourceHeight)
  PROJECTION_SIDES.forEach((side) => {
    const sideGeometry = displaySide(side)
    const line = outerBoundary?.sides[side] ?? sideGeometry?.outer_line
    if (line) drawSparseCurve(ctx, line, sourceWidth, fit.scaleX)
  })
  ctx.restore()

  drawProjectedLabels(ctx, projection, geometry, fit)
}

function drawSparseCurve(
  ctx: CanvasRenderingContext2D,
  line: BorderLine,
  sourceWidth: number,
  displayScale: number,
) {
  if (!isValidCenteringCurve(line) || displayScale <= Number.EPSILON) return
  const ranges = getSparseCenteringCurveRanges(line, sourceWidth)
  if (!ranges) return

  ctx.save()
  ctx.strokeStyle = CENTERING_COLOR
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  ctx.lineWidth = SPARSE_CONNECTOR_LINE_WIDTH / displayScale
  ctx.setLineDash(SPARSE_LINE_DASH.map((value) => value / displayScale))
  ranges.dashed.forEach((range) => {
    ctx.stroke(new Path2D(buildCenteringCurvePath(line, range.start, range.end)))
  })

  ctx.lineWidth = SPARSE_BOUNDARY_LINE_WIDTH / displayScale
  ctx.setLineDash([])
  ranges.solid.forEach((range) => {
    ctx.stroke(new Path2D(buildCenteringCurvePath(line, range.start, range.end)))
  })
  ctx.restore()
}

function drawProjectedLabels(
  ctx: CanvasRenderingContext2D,
  projection: CenteringProjection,
  geometry: CenteringProjectionGeometry | null,
  fit: Fit,
) {
  ;(['horizontal', 'vertical'] as const).forEach((axisName) => {
    const axis = preferredProjectionAxis(projection, axisName)
    const section = axis.available ? worstProjectionSection(axis) : null
    if (!section) return

    axis.labels.forEach((edge, index) => {
      const measurement = index === 0 ? section.first_segment : section.second_segment
      if (!geometry && !isValidCenteringCurve(measurement)) return
      const line = geometry?.sides[edge]?.inner_line ?? projection.sides[edge]?.inner_line
      const point = isValidCenteringCurve(line)
        ? getCenteringCurveMidpoint(line, edge)
        : isValidCenteringCurve(measurement)
          ? index === 0
            ? { x: measurement.x2, y: measurement.y2 }
            : { x: measurement.x1, y: measurement.y1 }
          : null
      if (!point) return
      const displayPoint = {
        x: fit.offsetX + point.x * fit.scaleX,
        y: fit.offsetY + point.y * fit.scaleY,
      }
      const value = index === 0 ? section.first_ratio : section.second_ratio
      drawProjectedLabel(ctx, edge, formatProjectedPercent(value), displayPoint, fit)
    })
  })
}

function drawProjectedLabel(
  ctx: CanvasRenderingContext2D,
  edge: BorderEdge,
  text: string,
  point: { x: number; y: number },
  fit: Fit,
) {
  const minLeft = fit.offsetX
  const maxLeft = fit.offsetX + fit.width - LABEL_WIDTH
  const minTop = fit.offsetY
  const maxTop = fit.offsetY + fit.height - LABEL_HEIGHT
  const slotLeft = clamp(point.x - LABEL_WIDTH / 2, minLeft, maxLeft)

  if (edge === 'left') {
    const left = clamp(point.x + LABEL_GAP, minLeft, maxLeft)
    const top = clamp(point.y - LABEL_HEIGHT / 2, minTop, maxTop)
    drawLabel(ctx, text, left, top, 'left', 'rgba(0, 0, 0, 0.72)')
    return
  }
  if (edge === 'right') {
    const left = clamp(point.x - LABEL_WIDTH - LABEL_GAP, minLeft, maxLeft)
    const top = clamp(point.y - LABEL_HEIGHT / 2, minTop, maxTop)
    drawLabel(ctx, text, left + LABEL_WIDTH, top, 'right', 'rgba(0, 0, 0, 0.72)')
    return
  }
  if (edge === 'top') {
    const top = clamp(point.y + LABEL_GAP, minTop, maxTop)
    drawLabel(ctx, text, slotLeft + LABEL_WIDTH / 2, top, 'center', 'rgba(0, 0, 0, 0.72)')
    return
  }

  const top = clamp(point.y - LABEL_HEIGHT - LABEL_GAP, minTop, maxTop)
  drawLabel(ctx, text, slotLeft + LABEL_WIDTH / 2, top, 'center', 'rgba(0, 0, 0, 0.72)')
}

function preferredProjectionAxis(
  projection: CenteringProjection,
  axis: 'horizontal' | 'vertical',
): CenteringProjectionAxis {
  const rectified = projection.rectified_anchor_measurement
  if (rectified?.coordinate_space === 'unit_card' && rectified.axes?.[axis]) {
    return rectified.axes[axis]
  }
  return projection.axes[axis]
}

function worstProjectionSection(axis: CenteringProjectionAxis): CenteringProjectionSection | null {
  if (!Array.isArray(axis.sections) || axis.sections.length === 0) return null
  if (
    typeof axis.worst_index === 'number' &&
    axis.worst_index >= 0 &&
    axis.worst_index < axis.sections.length
  ) {
    return axis.sections[axis.worst_index]
  }
  return axis.sections.reduce((worst, section) =>
    projectionSectionError(section) > projectionSectionError(worst) ? section : worst,
  )
}

function projectionSectionError(section: CenteringProjectionSection) {
  return Number.isFinite(section.error) ? Math.abs(section.error ?? 0) : Math.abs(section.first_ratio - 0.5)
}

function projectionToGeometry(projection: CenteringProjection): CenteringProjectionGeometry | null {
  const sides = {} as CenteringProjectionGeometry['sides']
  for (const side of PROJECTION_SIDES) {
    const projectedSide = projection.sides[side]
    if (
      !projectedSide?.available ||
      !isProjectionLineForVersion(projectedSide.outer_line, projection.version) ||
      !isProjectionLineForVersion(projectedSide.inner_line, projection.version)
    ) {
      return null
    }
    sides[side] = {
      outer_line: projectedSide.outer_line,
      inner_line: projectedSide.inner_line,
    }
  }
  return {
    version: projection.version,
    coordinate_space: projection.coordinate_space,
    image_shape: projection.image_shape,
    sides,
  }
}

function isProjectionLineForVersion(
  line: BorderLine | null | undefined,
  version: 1 | 2,
): line is BorderLine {
  if (!isValidCenteringCurve(line)) return false
  const hasMidpoint = Number.isFinite(line.xm) && Number.isFinite(line.ym)
  return version === 2 ? hasMidpoint : line.xm === undefined && line.ym === undefined
}

function canRenderProjection(projection: CenteringProjection) {
  if (!isProjectionStructureValid(projection)) return false
  return projectionToGeometry(projection) !== null
}

function isProjectionStructureValid(projection: CenteringProjection) {
  const shape = projection?.image_shape
  return Boolean(
    (projection.version === 1 || projection.version === 2) &&
      Array.isArray(shape) &&
      shape.length === 2 &&
      shape.every((value) => typeof value === 'number' && Number.isFinite(value) && value > 0) &&
      projection.sides &&
      projection.axes &&
      PROJECTION_SIDES.every((side) => Boolean(projection.sides[side])) &&
      (['horizontal', 'vertical'] as const).every((axis) =>
        Array.isArray(projection.axes[axis]?.sections),
      ),
  )
}

function applySourceFit(ctx: CanvasRenderingContext2D, fit: Fit) {
  ctx.translate(fit.offsetX, fit.offsetY)
  ctx.scale(fit.scaleX, fit.scaleY)
}

function clipProjectedOcclusions(
  ctx: CanvasRenderingContext2D,
  analysis: GradeSideAnalysis,
  projectionWidth: number,
  projectionHeight: number,
) {
  const analysisWidth = positive(analysis.sourceWidth) ?? projectionWidth
  const analysisHeight = positive(analysis.sourceHeight) ?? projectionHeight
  const maskRects = (analysis.damageMaskPaths ?? []).flatMap((svgPath) => {
    const rect = parseRectangularPath(svgPath)
    return rect ? [padDamageRect(rect, analysisWidth, analysisHeight)] : []
  })
  const detectionRects =
    maskRects.length > 0
      ? []
      : (analysis.damageDetections ?? []).flatMap((detection) => {
          const rect = normalizeBox(detection.box, analysisWidth, analysisHeight)
          return rect ? [rect] : []
        })
  const rects = [...maskRects, ...detectionRects]
  if (rects.length === 0) return

  const scaleX = projectionWidth / analysisWidth
  const scaleY = projectionHeight / analysisHeight
  const path = new Path2D()
  path.rect(0, 0, projectionWidth, projectionHeight)
  nonOverlappingRectUnion(rects).forEach((rect) => {
    path.rect(rect.x * scaleX, rect.y * scaleY, rect.width * scaleX, rect.height * scaleY)
  })
  ctx.clip(path, 'evenodd')
}

function nonOverlappingRectUnion(rects: readonly Rect[]): Rect[] {
  const xCoordinates = Array.from(
    new Set(rects.flatMap((rect) => [rect.x, rect.x + rect.width])),
  ).sort((first, second) => first - second)
  const union: Rect[] = []

  for (let index = 0; index < xCoordinates.length - 1; index += 1) {
    const left = xCoordinates[index]
    const right = xCoordinates[index + 1]
    if (right <= left) continue

    const intervals = rects
      .filter((rect) => rect.x < right && rect.x + rect.width > left)
      .map((rect) => ({ top: rect.y, bottom: rect.y + rect.height }))
      .sort((first, second) => first.top - second.top)
    if (intervals.length === 0) continue

    let top = intervals[0].top
    let bottom = intervals[0].bottom
    for (const interval of intervals.slice(1)) {
      if (interval.top <= bottom) {
        bottom = Math.max(bottom, interval.bottom)
        continue
      }
      union.push({ x: left, y: top, width: right - left, height: bottom - top })
      top = interval.top
      bottom = interval.bottom
    }
    union.push({ x: left, y: top, width: right - left, height: bottom - top })
  }

  return union
}

function clipToBoundary(
  ctx: CanvasRenderingContext2D,
  sidePoints: Record<BorderEdge, { x: number; y: number }[]>,
) {
  const points = [
    ...sidePoints.top,
    ...sidePoints.right.slice(1),
    ...[...sidePoints.bottom].reverse().slice(1),
    ...[...sidePoints.left].reverse().slice(1),
  ]
  if (points.length < 3) return
  ctx.beginPath()
  ctx.moveTo(points[0].x, points[0].y)
  points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y))
  ctx.closePath()
  ctx.clip()
}

function formatProjectedPercent(value: number) {
  const ratio = Number.isFinite(value) ? clamp(value, 0, 1) : 0.5
  return `${(ratio * 100).toFixed(1)}%`
}

function drawLegacyCentering(
  ctx: CanvasRenderingContext2D,
  analysis: GradeSideAnalysis,
  centering: CenteringSide | undefined,
  fit: Fit,
) {
  if (!hasCentering(analysis)) return

  const errorSides = new Set(analysis.borderErrorSides ?? [])
  const colorFor = (side: string) => (errorSides.has(side) ? CENTERING_WARNING : CENTERING_COLOR)
  const positions = centeringPositions(analysis, fit)
  const spans = centeringLineSpans(positions, fit)
  const segments = {
    leftOuter: { x1: positions.leftOuter, y1: spans.left.start, x2: positions.leftOuter, y2: spans.left.end },
    leftInner: { x1: positions.leftInner, y1: spans.left.start, x2: positions.leftInner, y2: spans.left.end },
    rightInner: { x1: positions.rightInner, y1: spans.right.start, x2: positions.rightInner, y2: spans.right.end },
    rightOuter: { x1: positions.rightOuter, y1: spans.right.start, x2: positions.rightOuter, y2: spans.right.end },
    topOuter: { x1: spans.top.start, y1: positions.topOuter, x2: spans.top.end, y2: positions.topOuter },
    topInner: { x1: spans.top.start, y1: positions.topInner, x2: spans.top.end, y2: positions.topInner },
    bottomInner: { x1: spans.bottom.start, y1: positions.bottomInner, x2: spans.bottom.end, y2: positions.bottomInner },
    bottomOuter: { x1: spans.bottom.start, y1: positions.bottomOuter, x2: spans.bottom.end, y2: positions.bottomOuter },
  }

  ctx.save()
  ctx.lineCap = 'butt'
  ctx.lineJoin = 'miter'
  ctx.lineWidth = CENTERING_LINE_WIDTH

  fillBand(ctx, segments.leftOuter, segments.leftInner)
  fillBand(ctx, segments.rightInner, segments.rightOuter)
  fillBand(ctx, segments.topOuter, segments.topInner)
  fillBand(ctx, segments.bottomInner, segments.bottomOuter)

  strokeSegment(ctx, segments.leftOuter, colorFor('left'))
  strokeSegment(ctx, segments.leftInner, colorFor('left'))
  strokeSegment(ctx, segments.rightInner, colorFor('right'))
  strokeSegment(ctx, segments.rightOuter, colorFor('right'))
  strokeSegment(ctx, segments.topOuter, colorFor('top'))
  strokeSegment(ctx, segments.topInner, colorFor('top'))
  strokeSegment(ctx, segments.bottomInner, colorFor('bottom'))
  strokeSegment(ctx, segments.bottomOuter, colorFor('bottom'))
  drawCenteringLabels(ctx, positions, spans, fit, centering)
  ctx.restore()
}

function drawCenteringLabels(
  ctx: CanvasRenderingContext2D,
  positions: ReturnType<typeof centeringPositions>,
  spans: ReturnType<typeof centeringLineSpans>,
  fit: Fit,
  centering: CenteringSide | undefined,
) {
  const values = {
    left: formatCenteringPercent(centering?.left),
    right: formatCenteringPercent(centering?.right),
    top: formatCenteringPercent(centering?.top),
    bottom: formatCenteringPercent(centering?.bottom),
  }
  const verticalMin = fit.offsetY
  const verticalMax = fit.offsetY + fit.height - LABEL_HEIGHT
  const horizontalMin = fit.offsetX
  const horizontalMax = fit.offsetX + fit.width
  const horizontalLabelMax = horizontalMax - LABEL_WIDTH
  const leftTop = clamp((spans.left.start + spans.left.end) / 2 - LABEL_HEIGHT / 2, verticalMin, verticalMax)
  const rightTop = clamp((spans.right.start + spans.right.end) / 2 - LABEL_HEIGHT / 2, verticalMin, verticalMax)
  const topLeft = clamp((spans.top.start + spans.top.end) / 2 - LABEL_WIDTH / 2, horizontalMin, horizontalLabelMax)
  const bottomLeft = clamp((spans.bottom.start + spans.bottom.end) / 2 - LABEL_WIDTH / 2, horizontalMin, horizontalLabelMax)

  drawLabel(ctx, values.left, positions.leftInner + LABEL_GAP, leftTop, 'left')
  drawLabel(ctx, values.right, positions.rightInner - LABEL_GAP, rightTop, 'right')
  drawLabel(ctx, values.top, topLeft + LABEL_WIDTH / 2, positions.topInner + LABEL_GAP, 'center')
  drawLabel(ctx, values.bottom, bottomLeft + LABEL_WIDTH / 2, positions.bottomInner - LABEL_GAP, 'bottom-center')
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  anchor: 'left' | 'right' | 'center' | 'bottom-center',
  background = 'rgba(0, 0, 0, 0.6)',
) {
  ctx.save()
  ctx.font = '800 10px Figtree, Arial, sans-serif'
  ctx.textBaseline = 'middle'
  const pillWidth = Math.min(LABEL_WIDTH, Math.max(32, Math.ceil(ctx.measureText(text).width) + 12))
  let left = x
  let top = y
  if (anchor === 'right') left = x - pillWidth
  if (anchor === 'center') left = x - pillWidth / 2
  if (anchor === 'bottom-center') {
    left = x - pillWidth / 2
    top = y - LABEL_HEIGHT
  }
  roundedRect(ctx, left, top, pillWidth, LABEL_HEIGHT, 4)
  ctx.fillStyle = background
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.fillText(text, left + pillWidth / 2, top + LABEL_HEIGHT / 2)
  ctx.restore()
}

function formatCenteringPercent(value: number | undefined) {
  return `${((value ?? 0.5) * 100).toFixed(1)}%`
}

function centeringPositions(
  analysis: GradeSideAnalysis,
  fit: Fit,
) {
  const lines = analysis.centeringLines ?? {}
  const ratios = analysis.borderWidthRatios ?? { h: 0.12, v: 0.12 }
  const useLineRatios = hasCenteringLines(lines)
  const coords = useLineRatios ? {} : (analysis.borderCoords ?? {})
  const mapX = (x: number) => x * fit.scaleX + fit.offsetX
  const mapY = (y: number) => y * fit.scaleY + fit.offsetY
  const left = coords.left?.length === 4 ? coords.left : null
  const right = coords.right?.length === 4 ? coords.right : null
  const top = coords.top?.length === 4 ? coords.top : null
  const bottom = coords.bottom?.length === 4 ? coords.bottom : null

  return {
    leftOuter: left ? mapX(left[0]) : fit.offsetX + (lines.leftOuterRatio ?? 0) * fit.width,
    leftInner: left ? mapX(left[2]) : fit.offsetX + (lines.leftInnerRatio ?? ratios.h) * fit.width,
    rightInner: right ? mapX(right[0]) : fit.offsetX + fit.width - (lines.rightInnerRatio ?? ratios.h) * fit.width,
    rightOuter: right ? mapX(right[2]) : fit.offsetX + fit.width - (lines.rightOuterRatio ?? 0) * fit.width,
    topOuter: top ? mapY(top[1]) : fit.offsetY + (lines.topOuterRatio ?? 0) * fit.height,
    topInner: top ? mapY(top[3]) : fit.offsetY + (lines.topInnerRatio ?? ratios.v) * fit.height,
    bottomInner: bottom ? mapY(bottom[1]) : fit.offsetY + fit.height - (lines.bottomInnerRatio ?? ratios.v) * fit.height,
    bottomOuter: bottom ? mapY(bottom[3]) : fit.offsetY + fit.height - (lines.bottomOuterRatio ?? 0) * fit.height,
    leftFullStartY: left ? mapY(left[1]) : fit.offsetY,
    leftFullEndY: left ? mapY(left[3]) : fit.offsetY + fit.height,
    rightFullStartY: right ? mapY(right[1]) : fit.offsetY,
    rightFullEndY: right ? mapY(right[3]) : fit.offsetY + fit.height,
    topFullStartX: top ? mapX(top[0]) : fit.offsetX,
    topFullEndX: top ? mapX(top[2]) : fit.offsetX + fit.width,
    bottomFullStartX: bottom ? mapX(bottom[0]) : fit.offsetX,
    bottomFullEndX: bottom ? mapX(bottom[2]) : fit.offsetX + fit.width,
  }
}

function centeringLineSpans(
  positions: ReturnType<typeof centeringPositions>,
  fit: Fit,
) {
  const targetLength = fit.width * MAX_LINE_LENGTH_RATIO
  const verticalMin = fit.offsetY
  const verticalMax = fit.offsetY + fit.height
  const horizontalMin = fit.offsetX
  const horizontalMax = fit.offsetX + fit.width

  return {
    left: targetSpan(positions.leftFullStartY, positions.leftFullEndY, targetLength, verticalMin, verticalMax),
    right: targetSpan(positions.rightFullStartY, positions.rightFullEndY, targetLength, verticalMin, verticalMax),
    top: targetSpan(positions.topFullStartX, positions.topFullEndX, targetLength, horizontalMin, horizontalMax),
    bottom: targetSpan(positions.bottomFullStartX, positions.bottomFullEndX, targetLength, horizontalMin, horizontalMax),
  }
}

function targetSpan(start: number, end: number, target: number, min: number, max: number): SideSpan {
  const available = max - min
  const length = Math.min(target, available)
  const center = (start + end) / 2
  let nextStart = center - length / 2
  let nextEnd = center + length / 2
  if (nextStart < min) {
    nextEnd += min - nextStart
    nextStart = min
  }
  if (nextEnd > max) {
    nextStart -= nextEnd - max
    nextEnd = max
  }
  return { start: nextStart, end: nextEnd }
}

function hasCentering(analysis: GradeSideAnalysis) {
  const lines = analysis.centeringLines ?? {}
  const coords = analysis.borderCoords
  return (
    Boolean(coords && Object.values(coords).some((value) => value && value.length === 4)) ||
    hasCenteringLines(lines) ||
    (analysis.borderErrorSides?.length ?? 0) > 0
  )
}

function hasCenteringLines(lines: GradeSideAnalysis['centeringLines']) {
  return Object.values(lines ?? {}).some((value) => value !== undefined)
}

function fillBand(ctx: CanvasRenderingContext2D, first: Segment, second: Segment) {
  ctx.fillStyle = CENTERING_FILL
  ctx.beginPath()
  ctx.moveTo(first.x1, first.y1)
  ctx.lineTo(first.x2, first.y2)
  ctx.lineTo(second.x2, second.y2)
  ctx.lineTo(second.x1, second.y1)
  ctx.closePath()
  ctx.fill()
}

function strokeSegment(ctx: CanvasRenderingContext2D, segment: Segment, color: string) {
  ctx.strokeStyle = color
  ctx.beginPath()
  ctx.moveTo(segment.x1, segment.y1)
  ctx.lineTo(segment.x2, segment.y2)
  ctx.stroke()
}

function calculateContainFit(
  sourceWidth: number,
  sourceHeight: number,
  containerWidth: number,
  containerHeight: number,
): Fit {
  const sourceAspect = sourceWidth / sourceHeight
  const containerAspect = containerWidth / containerHeight
  let width: number
  let height: number
  let offsetX: number
  let offsetY: number

  if (sourceAspect > containerAspect) {
    width = containerWidth
    height = containerWidth / sourceAspect
    offsetX = 0
    offsetY = (containerHeight - height) / 2
  } else {
    height = containerHeight
    width = containerHeight * sourceAspect
    offsetX = (containerWidth - width) / 2
    offsetY = 0
  }

  return { scaleX: width / sourceWidth, scaleY: height / sourceHeight, offsetX, offsetY, width, height }
}

function parseRectangularPath(svgPath: string): Rect | null {
  const normalized = svgPath.trim().replace(/,/g, ' ')
  const match = normalized.match(
    /^M\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s+L\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s+L\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s+L\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s+Z$/i,
  )
  if (!match) return null
  const points = [
    { x: Number(match[1]), y: Number(match[2]) },
    { x: Number(match[3]), y: Number(match[4]) },
    { x: Number(match[5]), y: Number(match[6]) },
    { x: Number(match[7]), y: Number(match[8]) },
  ]
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const left = Math.min(...xs)
  const right = Math.max(...xs)
  const top = Math.min(...ys)
  const bottom = Math.max(...ys)
  if (right <= left || bottom <= top) return null
  return { x: left, y: top, width: right - left, height: bottom - top }
}

function normalizeBox(box: DamageBox, sourceWidth: number, sourceHeight: number): Rect | null {
  const maxCoord = Math.max(Math.abs(box.x1), Math.abs(box.x2), Math.abs(box.y1), Math.abs(box.y2))
  const scaleX = maxCoord <= 1.5 ? sourceWidth : 1
  const scaleY = maxCoord <= 1.5 ? sourceHeight : 1
  const left = Math.min(box.x1, box.x2) * scaleX
  const right = Math.max(box.x1, box.x2) * scaleX
  const top = Math.min(box.y1, box.y2) * scaleY
  const bottom = Math.max(box.y1, box.y2) * scaleY
  if (right <= left || bottom <= top) return null
  return { x: left, y: top, width: right - left, height: bottom - top }
}

function padDamageRect(rect: Rect, sourceWidth: number, sourceHeight: number): Rect {
  const shortSide = Math.min(rect.width, rect.height)
  const padding = clamp(shortSide * 0.14, 4, 12)
  const safeInsetX = sourceWidth * 0.005
  const safeInsetY = sourceHeight * 0.005
  const left = clamp(rect.x - padding, safeInsetX, sourceWidth - safeInsetX)
  const top = clamp(rect.y - padding, safeInsetY, sourceHeight - safeInsetY)
  const right = clamp(rect.x + rect.width + padding, safeInsetX, sourceWidth - safeInsetX)
  const bottom = clamp(rect.y + rect.height + padding, safeInsetY, sourceHeight - safeInsetY)
  return { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) }
}

function sourceRectToDisplayRect(rect: Rect, fit: Fit): Rect {
  const x = rect.x * fit.scaleX + fit.offsetX
  const y = rect.y * fit.scaleY + fit.offsetY
  return { x, y, width: rect.width * fit.scaleX, height: rect.height * fit.scaleY }
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const right = x + width
  const bottom = y + height
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(right - radius, y)
  ctx.quadraticCurveTo(right, y, right, y + radius)
  ctx.lineTo(right, bottom - radius)
  ctx.quadraticCurveTo(right, bottom, right - radius, bottom)
  ctx.lineTo(x + radius, bottom)
  ctx.quadraticCurveTo(x, bottom, x, bottom - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
}

function positive(value: number | undefined) {
  return value && value > 0 ? value : undefined
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}
